import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Onboarding steps based on the UI flow
export enum OnboardingStep {
  WELCOME = 'welcome',
  OVERVIEW_CHART = 'overview_chart',
  SPENDING_DISTRIBUTION = 'spending_distribution',
  SPENDING_TRENDS = 'spending_trends',
  ASSET_OVERVIEW = 'asset_overview',
  ASSET_FLUCTUATION = 'asset_fluctuation',
  ADD_TRANSACTION = 'add_transaction',
  COMPLETED = 'completed',
}

// Step order for navigation
const STEP_ORDER: OnboardingStep[] = [
  OnboardingStep.WELCOME,
  OnboardingStep.OVERVIEW_CHART,
  OnboardingStep.SPENDING_DISTRIBUTION,
  OnboardingStep.SPENDING_TRENDS,
  OnboardingStep.ASSET_OVERVIEW,
  OnboardingStep.ASSET_FLUCTUATION,
  OnboardingStep.ADD_TRANSACTION,
  OnboardingStep.COMPLETED,
];

interface OnboardingContextType {
  // State
  isOnboardingActive: boolean;
  currentStep: OnboardingStep;
  isSampleDataLoaded: boolean;
  hasCompletedOnboarding: boolean;
  showCompletionModal: boolean;

  // Actions
  startOnboarding: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: OnboardingStep) => void;
  skipOnboarding: () => void;
  exitSampleData: () => void;
  completeOnboarding: () => void;
  resetOnboarding: () => void;
  triggerCompletionModal: () => void;
  hideCompletionModal: () => void;

  // Helpers
  getCurrentStepIndex: () => number;
  getTotalSteps: () => number;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const ONBOARDING_STORAGE_KEY = 'mimo_onboarding_completed';
const SAMPLE_DATA_STORAGE_KEY = 'mimo_sample_data_loaded';

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.WELCOME);
  const [isSampleDataLoaded, setIsSampleDataLoaded] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    const sampleData = localStorage.getItem(SAMPLE_DATA_STORAGE_KEY);

    setHasCompletedOnboarding(completed === 'true');
    setIsSampleDataLoaded(sampleData === 'true');
  }, []);

  const startOnboarding = useCallback(() => {
    setIsOnboardingActive(true);
    setCurrentStep(OnboardingStep.WELCOME);
    setIsSampleDataLoaded(true);
    localStorage.setItem(SAMPLE_DATA_STORAGE_KEY, 'true');
  }, []);

  const nextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const previousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const goToStep = useCallback((step: OnboardingStep) => {
    setCurrentStep(step);
  }, []);

  const skipOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    localStorage.removeItem(SAMPLE_DATA_STORAGE_KEY);
  }, []);

  const exitSampleData = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    localStorage.removeItem(SAMPLE_DATA_STORAGE_KEY);
  }, []);

  const completeOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setIsSampleDataLoaded(false);
    setCurrentStep(OnboardingStep.COMPLETED);
    setHasCompletedOnboarding(true);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    localStorage.removeItem(SAMPLE_DATA_STORAGE_KEY);
  }, []);

  const resetOnboarding = useCallback(() => {
    setIsOnboardingActive(false);
    setCurrentStep(OnboardingStep.WELCOME);
    setIsSampleDataLoaded(false);
    setHasCompletedOnboarding(false);
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    localStorage.removeItem(SAMPLE_DATA_STORAGE_KEY);
  }, []);

  const triggerCompletionModal = useCallback(() => {
    setIsOnboardingActive(false);
    setShowCompletionModal(true);
  }, []);

  const hideCompletionModal = useCallback(() => {
    setShowCompletionModal(false);
  }, []);

  const getCurrentStepIndex = useCallback(() => {
    return STEP_ORDER.indexOf(currentStep);
  }, [currentStep]);

  const getTotalSteps = useCallback(() => {
    // Exclude WELCOME and COMPLETED from count
    return STEP_ORDER.length - 2;
  }, []);

  const isFirstStep = useCallback(() => {
    return currentStep === OnboardingStep.WELCOME || currentStep === OnboardingStep.OVERVIEW_CHART;
  }, [currentStep]);

  const isLastStep = useCallback(() => {
    return currentStep === OnboardingStep.ADD_TRANSACTION;
  }, [currentStep]);

  const value: OnboardingContextType = {
    isOnboardingActive,
    currentStep,
    isSampleDataLoaded,
    hasCompletedOnboarding,
    showCompletionModal,
    startOnboarding,
    nextStep,
    previousStep,
    goToStep,
    skipOnboarding,
    exitSampleData,
    completeOnboarding,
    resetOnboarding,
    triggerCompletionModal,
    hideCompletionModal,
    getCurrentStepIndex,
    getTotalSteps,
    isFirstStep,
    isLastStep,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
